import { useEffect } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Clock,
  MapPin,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ChatWidget } from "@/components/site/ChatWidget";
import { Reveal } from "@/components/site/Reveal";
import { vaiA } from "@/lib/scroll";
import { TELEFONO, TELEFONO_HREF } from "@/data/citta";
import { PAGINE_SERVIZI } from "@/data/servizi_pagine";
import { MENU_SERVIZI, SERVIZIO_SELECT_MAP } from "@/data/menu";
import { VOCE_A_PAGINA } from "@/data/sottopagine";

export default function ServicePage({ slugFisso }) {
  const { slug: slugParam } = useParams();
  const navigate = useNavigate();
  const slug = slugFisso || slugParam;
  const pagina = PAGINE_SERVIZI[slug];

  useEffect(() => {
    if (pagina) {
      document.title = `${pagina.titolo} — SOS Riparazioni 24`;
      window.scrollTo(0, 0);
    }
  }, [pagina]);

  if (!pagina) return <Navigate to="/" replace />;

  const altri = MENU_SERVIZI.filter((m) => m.slug !== slug);

  const richiedi = (voce) => {
    navigate("/");
    setTimeout(() => {
      vaiA("#contatti");
      window.dispatchEvent(
        new CustomEvent("precompila-servizio", {
          detail: { servizio: pagina.mappaSelect || SERVIZIO_SELECT_MAP[slug] || "", voce },
        })
      );
    }, 500);
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid={`servizio-page-${slug}`}>
      <Header />
      <main>
        <section className="relative flex min-h-[75vh] items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={pagina.immagine}
              alt={`${pagina.nome} di pronto intervento al lavoro`}
              className="h-full w-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/70 to-obsidian" />
            <div className="absolute left-1/2 top-1/3 h-96 w-96 -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]" />
          </div>
          <div className="relative z-10 mx-auto w-full max-w-5xl px-5 pb-16 pt-40 text-center md:px-8">
            <Reveal>
              <Link
                to="/"
                data-testid="servizio-torna-home"
                className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-amber-400"
              >
                <ArrowLeft size={15} /> Torna alla home
              </Link>
              <p className="overline-label">Pronto intervento H24 · Lombardia</p>
              <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white text-balance sm:text-5xl lg:text-6xl">
                {pagina.titolo.split("H24")[0]}
                {pagina.titolo.includes("H24") && <span className="text-amber-400">H24</span>}
                {pagina.titolo.split("H24")[1] || ""}
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                {pagina.intro}
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <a
                  href={TELEFONO_HREF}
                  data-testid="servizio-cta-chiama"
                  className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
                >
                  <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
                </a>
                <button
                  type="button"
                  data-testid="servizio-cta-chat"
                  onClick={() => window.dispatchEvent(new CustomEvent("apri-chat"))}
                  className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-all hover:border-amber-400 hover:text-amber-400"
                >
                  <MessageCircle size={18} /> Chatta con noi
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
          <Reveal className="text-center">
            <p className="overline-label">Interventi</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Cosa risolviamo, <span className="text-amber-400">a qualsiasi ora.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pagina.voci.map((v, i) => (
              <Reveal key={v.t} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`servizio-voce-${i}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-800 bg-slatecard p-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/50"
                >
                  <h3 className="font-display text-lg font-semibold text-white">{v.t}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-400">{v.d}</p>
                  {VOCE_A_PAGINA[slug]?.[v.t] ? (
                    <Link
                      to={VOCE_A_PAGINA[slug][v.t]}
                      data-testid={`servizio-voce-cta-${i}`}
                      className="mx-auto mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 transition-colors hover:text-amber-300"
                    >
                      Apri la pagina dedicata
                      <ArrowUpRight size={15} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                  ) : (
                    <button
                      type="button"
                      data-testid={`servizio-voce-cta-${i}`}
                      onClick={() => richiedi(v.t)}
                      className="mx-auto mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-400 transition-colors hover:text-amber-300"
                    >
                      Richiedi questo intervento
                      <ArrowUpRight size={15} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </button>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="border-y border-slate-800 bg-abyss py-20 md:py-24">
          <div className="mx-auto max-w-5xl px-5 text-center md:px-8">
            <Reveal>
              <p className="overline-label">Perché noi</p>
              <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                La chiamata giusta <span className="text-amber-400">al momento giusto.</span>
              </h2>
            </Reveal>
            <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2">
              {pagina.perche.map((p) => (
                <Reveal key={p}>
                  <p className="flex items-center justify-center gap-2.5 rounded-xl border border-slate-800 bg-slatecard px-5 py-4 text-sm text-slate-200">
                    <CheckCircle2 size={17} className="shrink-0 text-emerald-400" />
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <div className="mx-auto mt-10 grid max-w-2xl grid-cols-3 gap-6 border-t border-slate-800 pt-8">
                {[
                  { icon: Clock, v: "H24/7", l: "Sempre operativi" },
                  { icon: MapPin, v: "12 città", l: "Coperte in Lombardia" },
                  { icon: ShieldCheck, v: "Garantito", l: "Tecnici assicurati" },
                ].map((s) => (
                  <div key={s.l} className="text-center">
                    <s.icon size={20} className="mx-auto mb-2 text-amber-400" />
                    <p className="font-display text-xl font-bold text-white">{s.v}</p>
                    <p className="mt-1 text-xs text-slate-400 sm:text-sm">{s.l}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 text-center md:px-8">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Ti serve aiuto <span className="text-amber-400">adesso?</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">
              Chiama o scrivici in chat: un operatore attiva subito il tecnico più vicino a te.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href={TELEFONO_HREF}
                data-testid="servizio-cta-finale-chiama"
                className="inline-flex items-center gap-2.5 rounded-full bg-amber-500 px-8 py-4 text-base font-bold text-obsidian transition-all hover:-translate-y-0.5 hover:bg-amber-400"
              >
                <Phone size={18} strokeWidth={2.5} /> Chiama ora · {TELEFONO}
              </a>
              <button
                type="button"
                data-testid="servizio-cta-finale-richiedi"
                onClick={() => richiedi(`Intervento ${pagina.nome.toLowerCase()}`)}
                className="inline-flex items-center gap-2.5 rounded-full border border-slate-600 px-8 py-4 text-base font-semibold text-white transition-all hover:border-amber-400 hover:text-amber-400"
              >
                Richiedi assistenza
              </button>
            </div>
            <div className="mt-12">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-400">
                Altri servizi H24
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-3">
                {altri.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/servizi/${m.slug}`}
                    data-testid={`servizio-altro-${m.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-amber-500 hover:text-amber-400"
                  >
                    {m.nome} <ArrowUpRight size={14} />
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
}
